if (!customElements.get('free-shipping-progress')) {
  customElements.define(
    'free-shipping-progress',
    class FreeShippingProgress extends HTMLElement {
      connectedCallback() {
        this.threshold = Number(this.dataset.threshold);
        this.currency = this.dataset.currency || 'USD';
        this.belowMessage = this.querySelector('[data-free-shipping-below]');
        this.completeMessage = this.querySelector('[data-free-shipping-complete]');
        this.amount = this.querySelector('[data-free-shipping-amount]');
        this.bar = this.querySelector('[data-free-shipping-bar]');
        this.track = this.querySelector('[role="progressbar"]');
        this.requestNumber = 0;
        this.refreshTimer = undefined;
        this.handleCartUpdate = () => this.scheduleRefresh();
        this.handleRebuyCartUpdate = () => this.scheduleRefresh(100);
        this.handlePageShow = (event) => {
          if (event.persisted) this.scheduleRefresh();
        };

        this.render(Number(this.dataset.cartTotal));

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
        }

        document.addEventListener('rebuy:cart.change', this.handleRebuyCartUpdate);
        window.addEventListener('pageshow', this.handlePageShow);
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        document.removeEventListener('rebuy:cart.change', this.handleRebuyCartUpdate);
        window.removeEventListener('pageshow', this.handlePageShow);
        window.clearTimeout(this.refreshTimer);
      }

      scheduleRefresh(delay = 0) {
        window.clearTimeout(this.refreshTimer);
        this.refreshTimer = window.setTimeout(() => this.refresh(), delay);
      }

      async refresh() {
        const requestNumber = ++this.requestNumber;
        const sectionId = this.closest('[data-section]')?.dataset.section;

        if (!sectionId) return;

        try {
          const sectionUrl = new URL(window.location.href);
          sectionUrl.searchParams.set('section_id', sectionId);

          const response = await fetch(sectionUrl.toString(), {
            headers: { Accept: 'text/html' },
            cache: 'no-store'
          });

          if (!response.ok) return;

          const sectionHtml = await response.text();
          if (requestNumber !== this.requestNumber) return;

          const parsedSection = new DOMParser().parseFromString(sectionHtml, 'text/html');
          const updatedProgress = parsedSection.querySelector('free-shipping-progress');

          if (!updatedProgress || !this.isConnected) return;

          this.replaceWith(updatedProgress.cloneNode(true));
        } catch (error) {
          console.error('Unable to update free shipping progress.', error);
        }
      }

      render(cartTotal) {
        if (!Number.isFinite(cartTotal) || !Number.isFinite(this.threshold) || this.threshold <= 0) return;

        const remaining = Math.max(this.threshold - cartTotal, 0);
        const progress = Math.min((cartTotal / this.threshold) * 100, 100);
        const isComplete = remaining === 0;

        this.classList.toggle('is-complete', isComplete);
        this.belowMessage.hidden = isComplete;
        this.completeMessage.hidden = !isComplete;
        this.amount.textContent = this.formatMoney(remaining);
        this.bar.style.width = `${progress}%`;
        this.track.setAttribute('aria-valuenow', Math.min(cartTotal, this.threshold));
        this.track.setAttribute(
          'aria-valuetext',
          isComplete ? 'Free shipping unlocked' : `${this.formatMoney(remaining)} remaining for free shipping`
        );
      }

      formatMoney(cents) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
            style: 'currency',
            currency: this.currency
          }).format(cents / 100);
        } catch (error) {
          return `$${(cents / 100).toFixed(2)}`;
        }
      }
    }
  );
}

if (!customElements.get('why-traditions')) {
  customElements.define(
    'why-traditions',
    class WhyTraditions extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('[data-why-traditions-toggle]');
        this.content = this.querySelector('[data-why-traditions-content]');
        if (!this.toggle || !this.content) return;

        this.mobileQuery = window.matchMedia('(max-width: 768px)');
        this.handleToggle = () => {
          this.setExpanded(this.toggle.getAttribute('aria-expanded') !== 'true');
        };
        this.handleViewportChange = () => this.syncLayout();

        this.toggle.addEventListener('click', this.handleToggle);
        if (this.mobileQuery.addEventListener) {
          this.mobileQuery.addEventListener('change', this.handleViewportChange);
        } else {
          this.mobileQuery.addListener(this.handleViewportChange);
        }

        this.syncLayout();
      }

      disconnectedCallback() {
        if (!this.toggle || !this.mobileQuery) return;

        this.toggle.removeEventListener('click', this.handleToggle);
        if (this.mobileQuery.removeEventListener) {
          this.mobileQuery.removeEventListener('change', this.handleViewportChange);
        } else {
          this.mobileQuery.removeListener(this.handleViewportChange);
        }
      }

      syncLayout() {
        const isMobile = this.mobileQuery.matches;
        this.toggle.hidden = !isMobile;
        this.setExpanded(!isMobile);
      }

      setExpanded(expanded) {
        this.toggle.setAttribute('aria-expanded', String(expanded));
        this.content.hidden = !expanded;
      }
    }
  );
}
